/**
 * Ordem oficial do questionário Kortex (template "Primeiro agente" / instruções do modelo).
 * Cada item usa a chave CSV em `header` e, se necessário, padrões no `question_text`.
 */

function normalizeForMatch(value) {
  return (value || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&#x2f;/gi, "/")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeCsvKey(value) {
  return normalizeForMatch(value).replace(/\s+/g, "_");
}

/**
 * @typedef {{ csvKeys: string[], questionPatterns: string[], onlyIfHeader?: string }} KortexTemplateEntry
 */

/** @type {KortexTemplateEntry[]} */
export const KORTEX_QUESTIONNAIRE_TEMPLATE_ORDER = [
  {
    csvKeys: ["certidao_transito_julgado"],
    questionPatterns: ["certidao de transito em julgado", "transito em julgado"],
  },
  {
    csvKeys: ["pedido_sentenca_execucao"],
    questionPatterns: [
      "pedido de cumprimento de sentenca",
      "cumprimento de sentenca / execucao",
      "cumprimento de sentenca/execucao",
    ],
  },
  {
    csvKeys: ["calculo_juntado"],
    questionPatterns: ["calculo juntado", "ha calculo juntado"],
  },
  {
    csvKeys: ["impugnacao_calculos"],
    questionPatterns: ["impugnacao aos calculos", "houve impugnacao"],
  },
  {
    csvKeys: ["homologacao_calculos"],
    questionPatterns: [
      "setor de contadoria",
      "setor de calculo judiciais",
      "perito contabil",
      "calculo judiciais",
    ],
  },
  {
    csvKeys: ["valor_homologado"],
    questionPatterns: [
      "houve homologacao dos calculos",
      "qual o valor homologado",
      "valor homologado",
    ],
  },
  {
    csvKeys: ["natureza_credito"],
    questionPatterns: ["natureza do credito"],
  },
  {
    csvKeys: ["cessao_credito"],
    questionPatterns: ["cessao de credito"],
  },
  {
    csvKeys: ["penhora_garantia"],
    questionPatterns: ["penhora ou garantia", "penhoras (bloqueios"],
  },
  {
    csvKeys: ["herdeiros"],
    questionPatterns: ["ha herdeiros"],
  },
  {
    csvKeys: ["honorarios_sucumbencia"],
    questionPatterns: ["honorarios de sucumbencia"],
  },
  {
    csvKeys: ["honorarios_contratuais"],
    questionPatterns: [
      "honorarios contratuais",
      "destaque de honorarios contratuais",
    ],
  },
  {
    csvKeys: ["expedicao_precatorio_rpv_alvara"],
    questionPatterns: [
      "expedicao de precatorio",
      "precatorio, rpv ou alvara",
      "rpv ou alvara",
    ],
  },
  {
    csvKeys: ["oficio_transmitido"],
    questionPatterns: [
      "oficio requisitorio",
      "foi expedido",
      "foi transmitido",
      "oficio transmitido",
    ],
  },
  {
    csvKeys: ["resumo"],
    questionPatterns: ["resumo integral do processo", "faca um resumo"],
  },
];

const UNKNOWN_ORDER_BASE = 1000;

function matchesEntryByHeader(entry, headerKey) {
  if (!headerKey) return false;
  return entry.csvKeys.some((key) => normalizeCsvKey(key) === headerKey);
}

function matchesEntryByQuestion(entry, questionNorm) {
  if (!questionNorm) return false;
  return entry.questionPatterns.some((pattern) =>
    questionNorm.includes(normalizeForMatch(pattern)),
  );
}

/**
 * Desambigua `homologacao_calculos` (contadoria) vs homologação/valor homologado.
 */
function resolveHomologacaoSlot(headerKey, questionNorm) {
  const contadoriaPatterns = KORTEX_QUESTIONNAIRE_TEMPLATE_ORDER[4].questionPatterns;
  const homologadoPatterns = KORTEX_QUESTIONNAIRE_TEMPLATE_ORDER[5].questionPatterns;

  if (contadoriaPatterns.some((p) => questionNorm.includes(normalizeForMatch(p)))) {
    return 4;
  }
  if (homologadoPatterns.some((p) => questionNorm.includes(normalizeForMatch(p)))) {
    return 5;
  }

  if (headerKey === "homologacao_calculos") {
    if (
      questionNorm.includes("houve homologacao") ||
      questionNorm.includes("homologacao dos calculos")
    ) {
      return 5;
    }
    return 4;
  }

  if (headerKey === "valor_homologado") {
    return 5;
  }

  return null;
}

/**
 * Índice de ordenação (0 = primeira pergunta do template).
 * @param {{ header?: string, question_text?: string }} item
 * @param {number} [fallbackIndex=0]
 */
export function resolveKortexQuestionSortIndex(item, fallbackIndex = 0) {
  const headerKey = normalizeCsvKey(item?.header);
  const questionNorm = normalizeForMatch(item?.question_text);

  if (headerKey === "homologacao_calculos" || headerKey === "valor_homologado") {
    const slot = resolveHomologacaoSlot(headerKey, questionNorm);
    if (slot != null) return slot;
  }

  for (let i = 0; i < KORTEX_QUESTIONNAIRE_TEMPLATE_ORDER.length; i++) {
    const entry = KORTEX_QUESTIONNAIRE_TEMPLATE_ORDER[i];
    if (matchesEntryByHeader(entry, headerKey)) {
      return i;
    }
  }

  for (let i = 0; i < KORTEX_QUESTIONNAIRE_TEMPLATE_ORDER.length; i++) {
    const entry = KORTEX_QUESTIONNAIRE_TEMPLATE_ORDER[i];
    if (matchesEntryByQuestion(entry, questionNorm)) {
      return i;
    }
  }

  return UNKNOWN_ORDER_BASE + fallbackIndex;
}

export function compareKortexQuestionnaireItems(a, b) {
  const aIndex =
    a?.__templateOrderIndex ??
    resolveKortexQuestionSortIndex(a, a?.__originalIndex ?? 0);
  const bIndex =
    b?.__templateOrderIndex ??
    resolveKortexQuestionSortIndex(b, b?.__originalIndex ?? 0);

  if (aIndex !== bIndex) return aIndex - bIndex;

  const aOrder = a?.__orderKey;
  const bOrder = b?.__orderKey;
  if (aOrder != null && bOrder != null) return aOrder - bOrder;
  if (aOrder != null) return -1;
  if (bOrder != null) return 1;

  return (a?.__originalIndex ?? 0) - (b?.__originalIndex ?? 0);
}
